import { StrictMode, useSyncExternalStore } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { LazyMotion, domAnimation } from 'motion/react'
import { Toaster } from 'sonner'

// Body/UI + display are variable fonts — one file covers 400/500/600/700.
import '@fontsource-variable/ibm-plex-sans/index.css'
import '@fontsource-variable/space-grotesk/index.css'
// Mono is non-variable — load the accent weights we actually use (400/500/600).
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import '@fontsource/ibm-plex-mono/600.css'
import './styles/globals.css'

import { router } from './app/router'
import { queryClient } from './app/query-client'
import { AuthProvider } from './app/auth-context'
import { getResolvedTheme, initTheme, subscribeTheme } from './app/theme'

/** Token-themed sonner toaster that follows the active theme. */
function AppToaster() {
  const theme = useSyncExternalStore(subscribeTheme, getResolvedTheme)
  return (
    <Toaster
      position="bottom-right"
      theme={theme}
      toastOptions={{
        style: {
          background: 'var(--bg-elev)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--sh-4)',
          fontFamily: 'var(--ff-ui)',
        },
      }}
    />
  )
}

/**
 * Keep the MSW dev worker mocking for the whole session.
 *
 * The service worker holds its set of "active" clients in memory. When the
 * browser terminates the idle worker (≈30s with no events) and restarts it to
 * serve a later fetch, that set is empty — so MSW passes every `/api` call
 * straight through to the Vite dev server, which answers a bare 404
 * "Not Found". The query client doesn't retry 404s, and a manual Retry just
 * re-fires the same bypassed request, so the page stays broken until a full
 * reload. (The client only announces itself via `MOCK_ACTIVATE` once, inside
 * `worker.start()`.)
 *
 * Two guards prevent that:
 *   1. A heartbeat postMessage keeps resetting the worker's idle timer while
 *      the tab is foregrounded, so it is never terminated mid-session.
 *   2. Background tabs throttle timers, so the worker can still be killed while
 *      hidden — re-run `worker.start()` on the way back to re-send
 *      `MOCK_ACTIVATE`. start() is idempotent, so repeat calls are safe.
 */
function keepMocksAlive(reactivate: () => Promise<unknown>) {
  const HEARTBEAT_MS = 15_000
  // Re-run start() (not a bare KEEPALIVE ping) so the client is *re-registered*,
  // not merely kept warm: a ping resets the worker's idle timer but never re-adds
  // this tab to activeClientIds, so a worker that restarted still bypasses every
  // request. start() re-sends MOCK_ACTIVATE and is idempotent.
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void reactivate().catch(() => {})
  }, HEARTBEAT_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void reactivate().catch(() => {})
  })
}

async function bootstrap() {
  initTheme()

  // Start MSW when VITE_USE_MOCKS is not 'false'. Dev defaults to on; the
  // production `-mocks` image (designer previews) sets it explicitly. The
  // prod flavor sets 'false' and skips.
  // The worker file is `public/mockServiceWorker.js` (generated by `npm run msw:init`).
  if (import.meta.env.VITE_USE_MOCKS !== 'false') {
    const { worker } = await import('./mocks/browser')
    const startOptions = {
      onUnhandledRequest: 'bypass',
      serviceWorker: { url: '/mockServiceWorker.js' },
    } as const
    await worker.start(startOptions)

    // A freshly-registered service worker doesn't *control* this page until it
    // claims it, which lands a beat after worker.start() resolves. If we render
    // now, React Query's first /api calls slip past MSW and every query fails —
    // the "everything failed to load, restart the dev server" symptom.
    //
    // The old fix reloaded once, guarded by a sticky sessionStorage flag. But if
    // that reload was *also* uncontrolled (a double race), the flag was already
    // set, so it gave up and the app rendered uncontrolled — permanently broken
    // until a manual restart. Instead, wait for the worker to claim this client:
    // controllerchange fires when the SW calls clients.claim() on activate. A
    // short cap keeps startup from hanging if claim never arrives, and there's
    // no sticky state, so any later reload self-heals.
    const MSW_BOOT_TRIES = 'it.msw.boot'
    if (!navigator.serviceWorker.controller) {
      // Wait for the worker to claim this client (controllerchange fires when the
      // SW calls clients.claim() on activate), capped so startup never hangs.
      await new Promise<void>((resolve) => {
        const settle = () => {
          navigator.serviceWorker.removeEventListener('controllerchange', settle)
          clearTimeout(timer)
          resolve()
        }
        const timer = setTimeout(settle, 3000)
        navigator.serviceWorker.addEventListener('controllerchange', settle)
      })

      // If the worker STILL isn't controlling this page, rendering now would let
      // the first /api calls bypass MSW (onUnhandledRequest: 'bypass') and hit the
      // Vite dev server → 404 "Not Found". The query client deliberately does not
      // retry 404s, so those widgets would stay broken until a manual Retry.
      // Reloading loads the page already controlled by the now-activated worker.
      // The counter bounds reloads so an environment where the SW can never take
      // control (e.g. unsupported) still renders instead of looping; it's cleared
      // below once we ARE controlled, so the next cold start can self-heal again.
      if (!navigator.serviceWorker.controller) {
        const tries = Number(sessionStorage.getItem(MSW_BOOT_TRIES) ?? '0')
        if (tries < 2) {
          sessionStorage.setItem(MSW_BOOT_TRIES, String(tries + 1))
          location.reload()
          return
        }
      }
    }
    // Controlled (or recovery exhausted): reset the guard for the next cold load.
    sessionStorage.removeItem(MSW_BOOT_TRIES)
    // Keep the worker mocking for the whole session (see keepMocksAlive).
    keepMocksAlive(() => worker.start({ ...startOptions, quiet: true }))
    // eslint-disable-next-line no-console
    console.info('[MSW] mocks active. Set VITE_USE_MOCKS=false to disable.')
  }

  const root = document.getElementById('root')!
  createRoot(root).render(
    <StrictMode>
      <LazyMotion features={domAnimation} strict>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
            <AppToaster />
          </AuthProvider>
        </QueryClientProvider>
      </LazyMotion>
    </StrictMode>,
  )
}

void bootstrap()
